import React, { useState } from 'react';
import { Formik, Form, FormikHelpers, Field } from 'formik';
import clsx from 'clsx';
import MaterialTable from 'material-table';
import dateFormat from 'dateformat';
import CssBaseline from '@material-ui/core/CssBaseline';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Container from '@material-ui/core/Container';
import { FormHelperText } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Check from '@material-ui/icons/Check';
import Remove from '@material-ui/icons/Remove';
import { TextField } from 'formik-material-ui';
import { Karte, CompleteProcessingRequestInfo } from 'openapi';

import ProcessingContainer from "../../containers/Processing";

interface Props {
  isLoading: boolean,
  karte?: Karte,
}

interface TableData {
  label: string,
  value: any,
}

interface FormValues {
  staffNo?: number;
  password?: number;
}

const useStyles = makeStyles(theme => ({
  paper: {
    marginTop: theme.spacing(8),
  },
  table: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(1),
  },
  form: {
    margin: theme.spacing(1, 0, 2),
  },
  isNotifiedLINEIcon: {
    textAlign: 'left',
    verticalAlign: 'middle',
    width: 48,
  },
  text: {
    margin: theme.spacing(1),
  },
  buttonSuccess: {
    backgroundColor: '#00B900',
    cursor: 'default',
    '&:hover': {
      backgroundColor: '#00B900',
    },
  },
  buttonWrapper: {
    position: 'relative',
  },
  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
}));

export default ((props) => {

  const processingContainer = ProcessingContainer.useContainer();
  const [isCompleted, setIsCompleted] = useState(false);
  const classes = useStyles();

  var handleSubmit = async (values: FormValues, formikHelpers: FormikHelpers<FormValues>) => {
    if (!isCompleted) {
      try {
        await processingContainer.completeProcessing({ ...props.karte, ...values } as CompleteProcessingRequestInfo);
        formikHelpers.setStatus('');
        setIsCompleted(true);
      } catch (err) {
        if (err.status === 403) {
          formikHelpers.setStatus('Invalid Staff No or Password');
        } else {
          formikHelpers.setStatus('Unhandled error');
        }

        formikHelpers.setSubmitting(false);
      }
    }
  }

  var tableData: TableData[] = !props.karte ? [] : [
    { label: 'カルテ番号', value: props.karte.karteNo },
    { label: '店舗番号', value: props.karte.storeCode },
    { label: '購入日', value: dateFormat(props.karte.createdDate, 'yyyy-mm-dd') },
    { label: 'お客様番号', value: props.karte.customerNo },
    { label: 'お客様名', value: props.karte.name },
    { label: 'レンズ名（左）', value: props.karte.leftLensName },
    { label: 'レンズ名（右）', value: props.karte.rightLensName },
    { label: 'フレーム名', value: props.karte.frameName },
  ];

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <div className={classes.paper}>
        <Typography component="h1" variant="h5">
          加工完了登録
            </Typography>
        <div className={classes.table}>
          <MaterialTable
            isLoading={props.isLoading}
            components={{ Toolbar: () => <></> }}
            columns={[
              { field: 'label' },
              { field: 'value' },
            ]}
            data={tableData}
            options={{ header: false, search: false, sorting: false, filtering: false, paging: false }}
          />
        </div>
        <div className={classes.table}>
          {!props.isLoading && props.karte && props.karte.processings && props.karte.processings.length > 0 &&
            <MaterialTable
              title='加工履歴'
              columns={[
                { title: '加工日', field: 'processedDate', render: rowData => <>{dateFormat(rowData.processedDate, 'yyyy-mm-dd HH:MM:ss')}</> },
                {
                  title: 'LINE通知', field: 'isNotifiedLINE', render: rowData => {
                    if (rowData.isNotifiedLINE) {
                      return <Check className={classes.isNotifiedLINEIcon} htmlColor={'#00B900'} />;
                    } else {
                      return <Remove className={classes.isNotifiedLINEIcon} />;
                    }
                  }
                },
              ]}
              data={props.karte.processings || []}
              options={{ search: false, sorting: false, filtering: false, paging: false }}
            />}
        </div>
        {(!props.isLoading && props.karte) &&
          <Formik
            initialValues={{ staffNo: undefined, password: undefined } as FormValues}
            onSubmit={handleSubmit}>
            {({ errors, touched, status, isSubmitting }) => (
              <Form className={classes.form}>
                <FormHelperText error={true}></ FormHelperText>
                <Field required
                  validate={(value: string) => !value ? 'Required' : undefined}
                  error={errors.staffNo && touched.staffNo}
                  helperText={errors.staffNo && touched.staffNo && errors.staffNo}
                  type='tel' pattern='[0-9]*' inputMode='numeric'
                  id='staffNo' name='staffNo' label='Staff No'
                  className={classes.text}
                  component={TextField} />
                <Field required
                  validate={(value: string) => !value ? 'Required' : undefined}
                  error={errors.password && touched.password}
                  helperText={errors.password && touched.password && errors.password}
                  autoComplete='current-password'
                  type='password' pattern='[0-9]*' inputMode='numeric'
                  id='password' name='password' label='Password'
                  className={classes.text}
                  component={TextField} />
                <div className={classes.buttonWrapper}>
                  <FormHelperText error={true}>{status}</ FormHelperText>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    color="primary"
                    className={clsx({ [classes.buttonSuccess]: isCompleted, })}
                    disableElevation={isCompleted}
                    disableTouchRipple={isCompleted}
                    disabled={isSubmitting}>
                    加工完了
                  </Button>
                  {isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
                </div>
              </Form>
            )}
          </Formik>
        }
      </div>
    </Container>
  );
}) as React.FC<Props>;
